/**
 * Auth0 Authentication Hook
 * Provides authentication state and methods using Auth0 React SDK
 */

import { useAuth0 } from '@auth0/auth0-react';
import { Browser } from '@capacitor/browser';
import { useCallback } from 'react';
import { isNativeMobile } from '@/utils/capacitor';
import { queryClient } from '@/lib/queryClient';
import { clearStoredDigest } from '@/lib/digestClient';
import { RecipeService } from '@/lib/recipeService';
import { CollectionService } from '@/lib/collectionService';

export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error
  } = useAuth0();

  const login = (returnTo?: string) => {
    loginWithRedirect({
      appState: {
        returnTo: returnTo || window.location.pathname + window.location.search
      },
      // On native, open Auth0 in Chrome Custom Tabs (Browser.open) so the React
      // app stays alive in the background. Without this, loginWithRedirect uses
      // window.location.assign which navigates the WebView away, destroying the
      // appUrlOpen listener in Auth0CallbackHandler before the callback fires.
      ...(isNativeMobile() && {
        openUrl: async (url: string) => {
          await Browser.open({ url, windowName: '_self' });
        },
      }),
    });
  };

  const logout = () => {
    // Clear all caches so the next user on this device starts fresh.
    queryClient.clear();
    clearStoredDigest();
    RecipeService.clear();
    CollectionService.clear();
    auth0Logout({
      logoutParams: {
        // On native, window.location.origin is https://localhost (Capacitor local server)
        // which won't match any Auth0 Allowed Logout URL. Use the custom scheme instead.
        returnTo: isNativeMobile() ? 'com.bytepantry://callback' : window.location.origin,
      },
      ...(isNativeMobile() && {
        openUrl: async (url: string) => {
          await Browser.open({ url, windowName: '_self' });
        },
      }),
    });
  };

  /**
   * Get an access token, attempting silent refresh first.
   * If we detect certain errors (login_required, consent_required, interaction_required)
   * we trigger an interactive login.
   */
  const getAccessToken = useCallback(async (options?: { ignoreCache?: boolean }): Promise<string | null> => {
    if (!isAuthenticated) return null;
    try {
      return await getAccessTokenSilently({
        cacheMode: options?.ignoreCache ? 'off' : 'on',
      });
    } catch (err) {
      const error = err as { error?: string; message?: string };
      const code = error?.error || error?.message || '';
      console.warn('Silent token acquisition failed:', code, error);
      // Only redirect to login for errors that genuinely require user interaction.
      // Redirecting on transient errors (network, timeouts) causes a loop: multiple
      // parallel queries can each call loginWithRedirect() before the app has settled
      // after a callback, sending the user back to Auth0 repeatedly.
      const requiresLogin = ['login_required', 'consent_required', 'interaction_required'].includes(code);
      if (requiresLogin) {
        try {
          await loginWithRedirect();
        } catch (loginErr) {
          console.error('Redirect login after silent failure also failed:', loginErr);
        }
      }
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  return {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    getAccessToken,
    error,
  };
}
